import { createElementFromHTML } from './utils'

interface IOptions {
  videoEl?: HTMLVideoElement
  loadTimeout?: number
  loadTimeoutHandler?: (el: HTMLVideoElement) => Promise<void>
  errorTimeout?: number
  errorEvents?: string[]
}

const htmlTemplate = `
  <video
    style="
      position:absolute;top:0px;left:0px;
      display:block;width:1px;height:1px;
      overflow:hidden;
    "
    controls="controls"
    muted="muted"
    playsinline="playsinline"
    webkit-playsinline="webkit-playsinline"
    x5-playsinline="x5-playsinline"
  >
  </video>
`
const defaultErrorTimeout = 3000
const defaultErrorEvents = ['error']

let innerVideoEl: null | HTMLVideoElement = null
let loadTimeoutTimer: any = null
let errorTimeoutTimer: any = null

export default function(src: string, options: IOptions = {}): Promise<HTMLVideoElement> {
  const { loadTimeout, loadTimeoutHandler, errorTimeout } = options
  const curVideoEl = getVideoEl(options)
  const curErrorTimeout = errorTimeout || defaultErrorTimeout

  clearAll(curVideoEl, options)

  return new Promise((resolve, reject) => {
    curVideoEl.onloadedmetadata = () => {
      clearAll(curVideoEl, options)
      resolve(curVideoEl)
    }

    curVideoEl.src = src

    if (loadTimeout && loadTimeoutHandler) {
      loadTimeoutTimer = setTimeout(async () => {
        await loadTimeoutHandler(curVideoEl)

        errorTimeoutTimer = setTimeout(() => {
          clearAll(curVideoEl, options)
          reject(new Error('Load Video Timeout'))
        }, curErrorTimeout)
      }, loadTimeout)
      return
    }

    addErrorEvents(curVideoEl, options, event => {
      clearAll(curVideoEl, options)
      reject('Load Video Error: ' + event)
    })

    errorTimeoutTimer = setTimeout(() => {
      clearAll(curVideoEl, options)
      reject(new Error('Load Video Timeout'))
    }, curErrorTimeout)
  })
}

export function delInnerVideoEl() {
  if (!innerVideoEl) {
    return
  }
  document.body.removeChild(innerVideoEl)
  innerVideoEl = null
}

export function warmUpInnerVideoEl(src: string) {
  const curVideoEl = getVideoEl({})
  curVideoEl.src = src
}

function getVideoEl(options: IOptions) {
  const { videoEl } = options
  if (videoEl) {
    return videoEl
  }
  if (!innerVideoEl) {
    innerVideoEl = createElementFromHTML<HTMLVideoElement>(htmlTemplate)
    document.body.appendChild(innerVideoEl)
  }
  return innerVideoEl
}

function clearAll(video: HTMLVideoElement, options: IOptions) {
  video.pause()
  video.currentTime = 0

  clearNormalEvent(video)
  clearErrorEvents(video, options)
  clearTimeoutTimer()
}

function clearNormalEvent(video: HTMLVideoElement) {
  video.onloadedmetadata = null
}

function addErrorEvents(video: HTMLVideoElement, options: IOptions, callback: (event: string) => void) {
  const { errorEvents } = options
  const events = errorEvents || defaultErrorEvents

  events.forEach(_ => {
    (video as any)['on' + _] = () => {
      callback(_)
    }
  })
}

function clearErrorEvents(video: HTMLVideoElement, options: IOptions) {
  const { errorEvents } = options
  const events = errorEvents ? [ ...defaultErrorEvents, ...errorEvents ] : [ ...defaultErrorEvents ]
  events.forEach(_ => {
    (video as any)['on' + _] = null
  })
}

function clearTimeoutTimer() {
  clearTimeout(loadTimeoutTimer)
  loadTimeoutTimer = null
  clearTimeout(errorTimeoutTimer)
  errorTimeoutTimer = null
}
